import { supabase } from '../lib/supabase'
import { isSupportCenterFormKind } from '../types/supportCenterSpecialties'
import type {
  SupportCenterForm,
  SupportCenterFormInput,
  SupportCenterFormRef,
  SupportCenterFormTableNames,
} from '../types/supportCenterForms'

const FORM_KEY_PATTERN = /^[a-z0-9_]+$/
const TABLE_NAME_PATTERN = /^[a-z][a-z0-9_]*$/

function normalizeFormKey(value: string): string {
  const key = value.trim().toLowerCase().replace(/\s+/g, '')
  if (!key) throw new Error('Informe a key do formulário.')
  if (!FORM_KEY_PATTERN.test(key)) {
    throw new Error('A key do formulário deve usar apenas letras minúsculas, números e _.')
  }
  return key
}

function normalizeTableName(value: string, label: string): string {
  const name = value.trim()
  if (!name) throw new Error(`Informe a ${label}.`)
  if (!TABLE_NAME_PATTERN.test(name)) {
    throw new Error(
      `A ${label} deve começar com letra minúscula e usar apenas letras minúsculas, números e _.`
    )
  }
  return name
}

function mapForm(row: Record<string, unknown>): SupportCenterForm {
  return {
    form_key: String(row.form_key ?? ''),
    name: String(row.name ?? ''),
    table_name: String(row.table_name ?? ''),
    triage_table_name: String(row.triage_table_name ?? ''),
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
    deleted_at: (row.deleted_at as string | null) ?? null,
    deleted_by: (row.deleted_by as string | null) ?? null,
    deleted_reason: (row.deleted_reason as string | null) ?? null,
  }
}

function sortByName(rows: SupportCenterForm[]): SupportCenterForm[] {
  return [...rows].sort((a, b) =>
    a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
  )
}

function toRef(row: SupportCenterForm): SupportCenterFormRef {
  return {
    form_key: row.form_key,
    name: row.name,
    table_name: row.table_name,
    triage_table_name: row.triage_table_name,
  }
}

export const supportCenterFormsService = {
  async list(): Promise<SupportCenterForm[]> {
    const { data, error } = await supabase
      .from('support_center_forms')
      .select('*')
      .is('deleted_at', null)
      .order('name', { ascending: true })

    if (error) throw error
    return sortByName((data ?? []).map((row) => mapForm(row as Record<string, unknown>)))
  },

  async listRefs(): Promise<SupportCenterFormRef[]> {
    const rows = await this.list()
    return rows.map(toRef)
  },

  /**
   * Resolve table_name e triage_table_name de um formulário ativo pela form_key.
   */
  async resolveTableNames(formKey: string): Promise<SupportCenterFormTableNames> {
    const key = formKey.trim()
    if (!key) throw new Error('Informe a key do formulário.')

    const { data, error } = await supabase
      .from('support_center_forms')
      .select('form_key, table_name, triage_table_name')
      .eq('form_key', key)
      .is('deleted_at', null)
      .maybeSingle()

    if (error) throw error
    if (!data) {
      throw new Error(`Formulário "${key}" não encontrado no catálogo.`)
    }

    const table_name = String(data.table_name ?? '').trim()
    const triage_table_name = String(data.triage_table_name ?? '').trim()
    if (!table_name) {
      throw new Error(`Formulário "${key}" sem tabela de atendimento cadastrada.`)
    }
    if (!triage_table_name) {
      throw new Error(`Formulário "${key}" sem tabela de triagem cadastrada.`)
    }

    return {
      form_key: String(data.form_key ?? key),
      table_name,
      triage_table_name,
    }
  },

  /**
   * Resolve em lote as tabelas de várias form_keys ativas.
   * Keys ausentes ou sem tabela pedida não entram no Map.
   */
  async resolveTableNamesByKeys(
    formKeys: string[]
  ): Promise<Map<string, SupportCenterFormTableNames>> {
    const uniqueKeys = [...new Set(formKeys.map((k) => k.trim()).filter(Boolean))]
    const result = new Map<string, SupportCenterFormTableNames>()
    if (uniqueKeys.length === 0) return result

    const { data, error } = await supabase
      .from('support_center_forms')
      .select('form_key, table_name, triage_table_name')
      .in('form_key', uniqueKeys)
      .is('deleted_at', null)

    if (error) throw error

    for (const row of data ?? []) {
      const mapped = mapForm(row as Record<string, unknown>)
      const table_name = mapped.table_name.trim()
      const triage_table_name = mapped.triage_table_name.trim()
      if (!mapped.form_key || !table_name || !triage_table_name) continue
      result.set(mapped.form_key, {
        form_key: mapped.form_key,
        table_name,
        triage_table_name,
      })
    }

    return result
  },

  async getAttendanceTableName(formKey: string): Promise<string> {
    const names = await this.resolveTableNames(formKey)
    return names.table_name
  },

  async getTriageTableName(formKey: string): Promise<string> {
    const names = await this.resolveTableNames(formKey)
    return names.triage_table_name
  },

  async upsert(input: SupportCenterFormInput): Promise<string> {
    const name = input.name.trim()
    const form_key = normalizeFormKey(input.form_key)
    const table_name = normalizeTableName(input.table_name, 'tabela de atendimento')
    const triage_table_name = normalizeTableName(
      input.triage_table_name,
      'tabela de triagem'
    )
    if (!name) throw new Error('Informe o nome do formulário.')

    const originalKey = input.original_form_key?.trim()

    if (originalKey) {
      const payload = { name, form_key, table_name, triage_table_name }
      const { error } = await supabase
        .from('support_center_forms')
        .update(payload)
        .eq('form_key', originalKey)
        .is('deleted_at', null)
      if (error) throw error

      if (originalKey !== form_key && isSupportCenterFormKind(form_key)) {
        const { error: kindError } = await supabase
          .from('support_center_assistances')
          .update({ form_kind: form_key })
          .eq('specific_form_key', form_key)
        if (kindError) throw kindError
      }

      return form_key
    }

    const { error } = await supabase
      .from('support_center_forms')
      .insert({ name, form_key, table_name, triage_table_name })
    if (error) throw error
    return form_key
  },

  async softDelete(formKey: string, reason: string, userId: string): Promise<void> {
    const deleted_reason = reason.trim()
    const key = formKey.trim()
    if (!deleted_reason) throw new Error('Informe o motivo da exclusão.')
    if (!key) throw new Error('Informe a key do formulário.')

    const { count: assistanceCount, error: assistanceCountError } = await supabase
      .from('support_center_assistances')
      .select('id', { count: 'exact', head: true })
      .eq('specific_form_key', key)
      .is('deleted_at', null)

    if (assistanceCountError) throw assistanceCountError
    if ((assistanceCount ?? 0) > 0) {
      throw new Error(
        'Não é possível excluir: existem assistências ativas vinculadas a este formulário.'
      )
    }

    const { count: triageCount, error: triageCountError } = await supabase
      .from('support_center_triages')
      .select('id', { count: 'exact', head: true })
      .eq('form_key', key)
      .is('deleted_at', null)

    if (triageCountError) throw triageCountError
    if ((triageCount ?? 0) > 0) {
      throw new Error(
        'Não é possível excluir: existem triagens vinculadas a este formulário.'
      )
    }

    const { error } = await supabase
      .from('support_center_forms')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: userId,
        deleted_reason,
      })
      .eq('form_key', key)
      .is('deleted_at', null)

    if (error) throw error
  },
}
